import {
  CheckableFormElement,
  checkableFormElementStyles,
  heightLimitedFromElementStyles
} from "./chunk.DJPU2C2P.js";
import {
  conditionalRender
} from "./chunk.XNLGRHFL.js";
import {
  emit
} from "./chunk.HM4ZRC4T.js";
import {
  focusVisibleSelector
} from "./chunk.ZX7YPA4S.js";
import {
  __decorateClass,
  component_styles_default,
  theme_styles_default
} from "./chunk.U4VUMZVF.js";

// src/components/checkbox/checkbox.ts
import { html } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";

// src/components/checkbox/checkbox-svg-checked.ts
var checkbox_svg_checked_default = `<svg xmlns="http://www.w3.org/2000/svg" width="10" height="8" viewBox="0 0 10 8">
										<path
											fill-rule="evenodd"
											clip-rule="evenodd"
											d="M9.37709 1.37698L4.00024 7.50014L0.688477 4.44389L1.68592 3.18582L3.88244 5.1268L8.26185 0.261719L9.37709 1.37698Z"
											fill="white"
										/>
									</svg>`;

// src/components/checkbox/checkbox-svg-indeterminate.ts
var checkbox_svg_indeterminate_default = `<svg xmlns="http://www.w3.org/2000/svg"
										width="8"
										height="2"
										viewBox="0 0 8 2"
										fill="none"
									>
										<rect width="8" height="2" fill="white" />
									</svg>`;

// src/components/checkbox/checkbox.styles.ts
import { css } from "lit";
var checkbox_styles_default = css`
	${theme_styles_default}
	${component_styles_default}
	${checkableFormElementStyles}
	${heightLimitedFromElementStyles}

	.checkbox__control {
		flex: 0 0 auto;
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: var(--yotpo-toggle-size);
		height: var(--yotpo-toggle-size);
		border-width: var(--yotpo-input-border-width);
		border-style: var(--yotpo-input-border-style);
		border-color: var(--yotpo-color-brand-30);
		border-radius: var(--yotpo-input-border-radius);
		background-color: var(--yotpo-input-background-color);
		transition: var(--yotpo-transition-fast) border-color, var(--yotpo-transition-fast) background-color,
			var(--yotpo-transition-fast) color, var(--yotpo-transition-fast) box-shadow;
	}

	.checkbox__input {
		position: absolute;
		opacity: 0;
		padding: 0;
		margin: 0;
		pointer-events: none;
	}

	.checkbox__control .checkbox__icon {
		display: inline-flex;
	}

	.checkbox__control .checkbox__icon svg {
		width: 100%;
		height: 100%;
	}

	/* Hover */
	.checkable_form_element:not(.checkable_form_element--checked):not(.checkable_form_element--disabled):hover
		.checkbox__control {
		border-color: var(--yotpo-color-brand-50);
	}

	/* Focus */
	.checkable_form_element:not(.checkable_form_element--checked):not(.checkable_form_element--disabled)
		.checkbox__input${focusVisibleSelector}
		~ .checkbox__control {
		box-shadow: var(--yotpo-focus-global);
		border-color: var(--yotpo-color-brand-50);
	}

	/* Checked/indeterminate */
	.checkable_form_element--checked .checkbox__control,
	.checkbox--indeterminate .checkbox__control {
		border-color: var(--yotpo-color-brand-70);
		background-color: var(--yotpo-color-brand-70);
		color: var(--yotpo-color-white);
	}

	/* Checked/indeterminate + hover */
	.checkable_form_element.checkable_form_element--checked:not(.checkable_form_element--disabled):hover
		.checkbox__control,
	.checkable_form_element.checkbox--indeterminate:not(.checkable_form_element--disabled):hover .checkbox__control {
		border-color: var(--yotpo-color-brand-60);
		background-color: var(--yotpo-color-brand-60);
	}

	/* Checked/indeterminate + focus */
	.checkable_form_element.checkable_form_element--checked:not(.checkable_form_element--disabled)
		.checkbox__input${focusVisibleSelector}
		~ .checkbox__control,
	.checkable_form_element.checkbox--indeterminate:not(.checkable_form_element--disabled)
		.checkbox__input${focusVisibleSelector}
		~ .checkbox__control {
		box-shadow: var(--yotpo-focus-global);
		border-color: var(--yotpo-color-brand-60);
		background-color: var(--yotpo-color-brand-60);
	}

	/* Default state + Disabled */
	.checkable_form_element.checkable_form_element--disabled .checkbox__control {
		border-color: var(--yotpo-color-disabled);
	}

	/* Checked + Disabled */
	.checkable_form_element.checkable_form_element--checked.checkable_form_element--disabled
		.checkbox__control
		.checkbox__icon,
	.checkable_form_element.checkbox--indeterminate.checkable_form_element--disabled
		.checkbox__control
		.checkbox__icon {
		background-color: var(--yotpo-color-disabled);
	}

	.checkable_form_element.checkable_form_element--checked.checkable_form_element--disabled .checkbox__control,
	.checkable_form_element.checkbox--indeterminate.checkable_form_element--disabled .checkbox__control {
		background-color: var(--yotpo-color-disabled);
	}
`;

// src/components/checkbox/checkbox.ts
var id = 0;
var CHECKBOX_INDETERMINATE_CLASS = "checkbox--indeterminate";
var YotpoCheckbox = class extends CheckableFormElement {
  constructor() {
    super(...arguments);
    this.svgChecked = checkbox_svg_checked_default;
    this.svgIndeterminate = checkbox_svg_indeterminate_default;
    this._indeterminate = false;
  }
  set indeterminate(newValue) {
    this.setIndeterminate(newValue);
  }
  get indeterminate() {
    return this._indeterminate;
  }
  setIndeterminate(newValue) {
    this.updateComplete.then(() => {
      this._indeterminate = newValue;
      this.input.indeterminate = this._indeterminate;
      this.invalid = !this.input.checkValidity();
      if (this._indeterminate) {
        this.base.classList.add(CHECKBOX_INDETERMINATE_CLASS);
      } else {
        this.base.classList.remove(CHECKBOX_INDETERMINATE_CLASS);
      }
    });
  }
  firstUpdated() {
    super.firstUpdated();
    this.setInputLabel(`checkbox-label-${id}`, `checkbox-${++id}`);
  }
  handleClick() {
    this.checked = !this.checked;
    this.indeterminate = false;
  }
  handleInputClick(event) {
    super.handleInputClick(event);
    const yotpoRequestChange = emit(this, "yotpo-request-change" /* requestChange */, { cancelable: true });
    if (yotpoRequestChange.defaultPrevented)
      return;
    this.handleClick();
  }
  render() {
    const checkboxInputAddition = html`
			<span part="control" class="checkbox__control">
				${conditionalRender(this.checked, html`<span part="checked-icon" class="checkbox__icon"> ${unsafeSVG(this.svgChecked)} </span>`)}
				${conditionalRender(!this.checked && this.indeterminate, html`<span part="indeterminate-icon" class="checkbox__icon">
						${unsafeSVG(this.svgIndeterminate)}
					</span>`)}
			</span>
		`;
    const checkboxData = {
      class: "checkbox__input",
      type: "checkbox",
      role: "checkbox",
      dataCy: "checkbox-input-el"
    };
    return this.renderCheckableFormElement(checkboxData, checkboxInputAddition);
  }
};
YotpoCheckbox.componentName = "Checkbox";
YotpoCheckbox.styles = checkbox_styles_default;
__decorateClass([
  query('input[type="checkbox"]')
], YotpoCheckbox.prototype, "input", 2);
__decorateClass([
  state()
], YotpoCheckbox.prototype, "_indeterminate", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], YotpoCheckbox.prototype, "indeterminate", 1);
YotpoCheckbox = __decorateClass([
  customElement("yotpo-checkbox")
], YotpoCheckbox);

export {
  YotpoCheckbox
};
